/**
 * PL に入る条件(2026-10-22 発注者決定)。PL・経費管理表・ダッシュボードの「実績/見込み」はすべてこの関数で判定する。
 *
 *   - 計上する月は計上日(posting_date)の月(案A)。承認日・支払日の月ではない
 *   - 見込み = 申請中・部長確認済み + 計上日の月がまだ来ていない「承認済み以降」
 *   - 実績   = 計上日の月が来た「承認済み以降」(承認済み・前渡し済み・受領済み・支払い済み・完了)
 *   - 対象外 = 差戻し・取消、資金移動(type='transfer')
 *   - 金額は取引の現在の金額(amount)。承認時は予定額、支払い後は確定額に置き換わる
 *   - 「月が来た」は今日(JST)の月が計上日の月以降であること。計上日が未来の月のものは、その月の1日から実績になる
 *
 * 売上・原価の直接入力・支払い済み申請(報告型)・繰り返し支払い・振込手数料も同じ規則で判定する
 * (いずれも承認済み以降の状態で作られるため、計上日の月が来ていれば実績)。
 */
import { todayStr, toMonth } from '@core/lib/date';
import {
  PL_APPROVED_STATUSES,
  PL_BEFORE_APPROVAL_STATUSES,
  type DateStr,
  type MonthStr,
  type Transaction,
  type Yen,
} from '@core/lib/types';

/** PL での扱い。excluded は PL に出さない */
export type PlCategory = 'actual' | 'forecast' | 'excluded';

export type PlEntry = {
  category: PlCategory;
  /** 計上する月(計上日の月) */
  month: MonthStr;
  /** 計上する金額(取引の現在の金額) */
  amount: Yen;
};

type PlInput = Pick<Transaction, 'status' | 'posting_date' | 'amount' | 'type'>;

/** 取引が PL で「実績」「見込み」「対象外」のどれか。today は省略時に今日(JST) */
export function plCategoryOf(t: Pick<Transaction, 'status' | 'posting_date' | 'type'>, today: DateStr = todayStr()): PlCategory {
  if (t.type === 'transfer') return 'excluded';
  if (PL_BEFORE_APPROVAL_STATUSES.includes(t.status)) return 'forecast';
  if (!PL_APPROVED_STATUSES.includes(t.status)) return 'excluded';
  return toMonth(t.posting_date) <= toMonth(today) ? 'actual' : 'forecast';
}

/** PL に載せる内容(扱い・月・金額)をまとめて返す */
export function plEntryOf(t: PlInput, today: DateStr = todayStr()): PlEntry {
  return { category: plCategoryOf(t, today), month: toMonth(t.posting_date), amount: t.amount };
}
